import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import type { AxiosError } from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
    Card,
    CardContent,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Skeleton,
    Button,
    Github,
} from '@pagespeedify/ui';
import {
    GitBranch,
    Settings,
    Loader2,
    CheckCircle2,
    ArrowRight,
    FileSearch,
    Zap,
} from 'lucide-react';
import { useApi } from '../../lib/apiClient';
import { useAuth } from '../../lib/auth/AuthContext';
import { toast } from 'sonner';
import PromptScopeSuggestions from '../agent/PromptScopeSuggestions';
import type {
    AgentExecuteScopeContext,
    AgentScopeAnswers,
    AgentSuperMemoryState,
    PromptNeedsScopePayload,
    PromptScopeSuggestion,
    PromptSpecialization
} from '../../types/agent';
import {
    buildPersonalizedScopeSuggestions,
    detectPromptSpecialization,
    getRepoScopedMemoryEntries,
    isActionableIntakeWarning,
    isLikelyVaguePrompt
} from '../../lib/agent/scopeSuggestionBuilder';


interface GitHubRepo {
    id: number;
    name: string;
    full_name: string;
    default_branch: string;
    private: boolean;
}

interface GitHubBranch {
    name: string;
    protected: boolean;
}

type ExecutionMode = 'review' | 'auto';

const EXECUTION_MODES: { value: ExecutionMode; label: string; description: string; icon: typeof FileSearch }[] = [
    { value: 'review', label: 'Review', description: 'Generate plan and wait for approval', icon: FileSearch },
    { value: 'auto', label: 'Auto', description: 'Get started without plan approval', icon: Zap },
];

const GITHUB_OAUTH_RETURN_KEY = 'swayashin_github_oauth_return';
const AGENT_PROMPT_DRAFT_STORAGE_PREFIX = 'swayashin_agent_prompt_draft_v1';
const AGENT_PROMPT_DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

interface PromptDraftPayload {
    prompt?: string;
    updatedAt?: number;
    lastSuggestionId?: string;
    specialization?: PromptSpecialization;
    scopeAnswers?: AgentScopeAnswers;
    memoryDerived?: boolean;
    monitoringId?: string;
    monitoringUrl?: string;
    monitoringEnabledForRun?: boolean;
}

function extractPromptScopePayload(error: unknown): PromptNeedsScopePayload | null {
    const axiosError = error as AxiosError<PromptNeedsScopePayload>;
    const data = axiosError?.response?.data;
    if (!data || data.error !== 'prompt_needs_scope') return null;
    if (!Array.isArray(data.suggestions) || !Array.isArray(data.requiredHints)) return null;
    return data;
}

interface AgentPromptCardProps {
    className?: string;
    /** If true, create task immediately instead of navigating */
    createTaskOnSubmit?: boolean;
    /** Default repo from user preferences */
    defaultRepo?: string;
    /** Default branch from user preferences */
    defaultBranch?: string;
}

/**
 * AgentPromptCard - Dashboard component for starting agent tasks
 * Matches the AgentPage UI: repo selector, mode selector, branch selector
 */
export default function AgentPromptCard({
    className = '',
    createTaskOnSubmit = false,
    defaultRepo,
    defaultBranch
}: AgentPromptCardProps) {
    const navigate = useNavigate();
    const location = useLocation();
    const apiClient = useApi();
    const { user } = useAuth();
    const userId = user?.uid || null;

    const [selectedRepo, setSelectedRepo] = useState<string>(defaultRepo || '');
    const [selectedBranch, setSelectedBranch] = useState<string>(defaultBranch || '');
    const [shouldFetchBranches, setShouldFetchBranches] = useState<boolean>(!!defaultRepo); // Preload if prop provided
    const [prompt, setPrompt] = useState('');
    const [scopeGatePayload, setScopeGatePayload] = useState<PromptNeedsScopePayload | null>(null);
    const [scopeContext, setScopeContext] = useState<AgentExecuteScopeContext | null>(null);
    const [selectedMonitoringId, setSelectedMonitoringId] = useState<string>('');
    const [selectedMonitoringUrl, setSelectedMonitoringUrl] = useState<string>('');
    const [monitoringEnabledForRun, setMonitoringEnabledForRun] = useState<boolean>(false);
    const [scopeAnswers, setScopeAnswers] = useState<AgentScopeAnswers>({});
    const [promptDraftMeta, setPromptDraftMeta] = useState<{
        lastSuggestionId?: string;
        specialization?: PromptSpecialization;
    }>({});
    const [executionMode, setExecutionMode] = useState<ExecutionMode>('review');
    const lastAuthenticatedUidRef = useRef<string | null>(null);
    const hasRestoredPromptDraftRef = useRef(false);
    const isFreeLikeUser = (user?.user_type || '').toLowerCase() === 'unsubscribed' || (user?.user_type || '').toLowerCase() === 'free';
    const agentPromptDraftStorageKey = useMemo(
        () => (userId ? `${AGENT_PROMPT_DRAFT_STORAGE_PREFIX}:${userId}` : null),
        [userId]
    );

    const clearPromptDraft = useCallback(() => {
        if (!agentPromptDraftStorageKey) return;
        try {
            localStorage.removeItem(agentPromptDraftStorageKey);
        } catch (error) {
            console.warn('[AgentPromptCard] Failed to clear prompt draft:', error);
        }
    }, [agentPromptDraftStorageKey]);

    const handleConnectGitHub = async () => {
        if (isFreeLikeUser) {
            toast.error('Upgrade your account to use Agent and connect GitHub.');
            navigate('/subscriptions');
            return;
        }
        try {
            const response = await apiClient.post<{ redirectUrl?: string; isAlreadyLinked?: boolean }>('/github/auth/start', {
                source: 'dashboard',
            });

            if (response?.redirectUrl) {
                try {
                    sessionStorage.setItem(
                        GITHUB_OAUTH_RETURN_KEY,
                        JSON.stringify({ path: '/user-dashboard', createdAt: Date.now() })
                    );
                } catch (storageError) {
                    console.warn('[AgentPromptCard] Failed to persist OAuth return path:', storageError);
                }
                window.location.href = response.redirectUrl;
                return;
            }

            if (response?.isAlreadyLinked) {
                toast.success('Your GitHub account is already linked.');
                return;
            }

            toast.error('Could not get GitHub authentication URL.');
        } catch (error) {
            console.error('[AgentPromptCard] Failed to start GitHub auth flow:', error);
            toast.error('Failed to start GitHub connection.');
        }
    };

    useEffect(() => {
        const previousUid = lastAuthenticatedUidRef.current;
        if (previousUid !== userId) {
            hasRestoredPromptDraftRef.current = false;
        }
        if (previousUid && previousUid !== userId) {
            setSelectedRepo(defaultRepo || '');
            setSelectedBranch(defaultBranch || '');
            setShouldFetchBranches(!!defaultRepo);
            setPrompt('');
            setScopeGatePayload(null);
            setScopeContext(null);
            setSelectedMonitoringId('');
            setSelectedMonitoringUrl('');
            setMonitoringEnabledForRun(false);
            setScopeAnswers({});
            setPromptDraftMeta({});
        }
        lastAuthenticatedUidRef.current = userId;
    }, [defaultBranch, defaultRepo, userId]);

    useEffect(() => {
        if (!agentPromptDraftStorageKey) return;
        if (hasRestoredPromptDraftRef.current) return;

        try {
            let draftPrompt = '';
            let parsed: Partial<PromptDraftPayload> = {};

            // 1. Check URL parameters for explicit intent and payload
            const searchParams = new URLSearchParams(location.search);
            if (searchParams.get('intent') === 'prompt') {
                const urlPayload = searchParams.get('payload');
                if (urlPayload) {
                    draftPrompt = urlPayload;
                    // Note: We remove it from URL so refreshing doesn't keep replacing drafts
                    window.history.replaceState({}, '', location.pathname);
                }
            }

            // 2. Check current user's localStorage draft if no URL draft
            if (!draftPrompt) {
                const raw = localStorage.getItem(agentPromptDraftStorageKey);
                if (raw) {
                    parsed = JSON.parse(raw) as PromptDraftPayload;
                    const updatedAt = Number(parsed?.updatedAt || 0);
                    const isFresh = Number.isFinite(updatedAt) && Date.now() - updatedAt <= AGENT_PROMPT_DRAFT_TTL_MS;
                    if (isFresh && typeof parsed.prompt === 'string') {
                        draftPrompt = parsed.prompt;
                    } else {
                        localStorage.removeItem(agentPromptDraftStorageKey);
                    }
                }
            }

            // 3. Fallback to a guest localStorage key in case app and admin share origins
            if (!draftPrompt) {
                const rawGuest = localStorage.getItem(`${AGENT_PROMPT_DRAFT_STORAGE_PREFIX}:guest`);
                if (rawGuest) {
                    const parsedGuest = JSON.parse(rawGuest) as PromptDraftPayload;
                    if (typeof parsedGuest.prompt === 'string') {
                        draftPrompt = parsedGuest.prompt;
                    }
                    localStorage.removeItem(`${AGENT_PROMPT_DRAFT_STORAGE_PREFIX}:guest`);
                }
            }

            if (draftPrompt) {
                setPrompt((currentValue) => currentValue || draftPrompt);
                const restoredMonitoringEnabled = parsed?.monitoringEnabledForRun === true;
                const restoredScopeContext: AgentExecuteScopeContext = {
                    ...(typeof parsed?.lastSuggestionId === 'string' && parsed.lastSuggestionId
                        ? { suggestionId: parsed.lastSuggestionId }
                        : {}),
                    ...(typeof parsed?.specialization === 'string'
                        ? { specialization: parsed.specialization as PromptSpecialization }
                        : {}),
                    ...(parsed?.scopeAnswers && typeof parsed.scopeAnswers === 'object'
                        ? { scopeAnswers: parsed.scopeAnswers as AgentScopeAnswers }
                        : {}),
                    ...(restoredMonitoringEnabled && typeof parsed?.monitoringId === 'string' && parsed.monitoringId
                        ? { monitoringId: parsed.monitoringId }
                        : {}),
                    ...(restoredMonitoringEnabled && typeof parsed?.monitoringUrl === 'string' && parsed.monitoringUrl
                        ? { monitoringUrl: parsed.monitoringUrl }
                        : {}),
                    ...(parsed?.memoryDerived === true ? { memoryDerived: true } : {})
                };
                if (
                    restoredScopeContext.suggestionId
                    || restoredScopeContext.specialization
                    || restoredScopeContext.scopeAnswers
                    || restoredScopeContext.memoryDerived
                ) {
                    setScopeContext(restoredScopeContext);
                }
                if (parsed?.scopeAnswers && typeof parsed.scopeAnswers === 'object') {
                    setScopeAnswers(parsed.scopeAnswers as AgentScopeAnswers);
                }
                if (typeof parsed?.monitoringId === 'string') {
                    setSelectedMonitoringId(parsed.monitoringId);
                }
                if (typeof parsed?.monitoringUrl === 'string') {
                    setSelectedMonitoringUrl(parsed.monitoringUrl);
                }
                setMonitoringEnabledForRun(restoredMonitoringEnabled);
                setPromptDraftMeta((current) => ({
                    ...current,
                    ...(typeof parsed?.lastSuggestionId === 'string' && parsed.lastSuggestionId
                        ? { lastSuggestionId: parsed.lastSuggestionId }
                        : {}),
                    ...(typeof parsed?.specialization === 'string'
                        ? { specialization: parsed.specialization as PromptSpecialization }
                        : {})
                }));
            } else {
                localStorage.removeItem(agentPromptDraftStorageKey);
            }
        } catch (error) {
            console.warn('[AgentPromptCard] Failed to restore prompt draft:', error);
            localStorage.removeItem(agentPromptDraftStorageKey);
        } finally {
            hasRestoredPromptDraftRef.current = true;
        }
    }, [agentPromptDraftStorageKey]);

    useEffect(() => {
        if (!agentPromptDraftStorageKey) return;
        if (!hasRestoredPromptDraftRef.current) return;

        try {
            if (!prompt.trim()) {
                localStorage.removeItem(agentPromptDraftStorageKey);
                return;
            }
            localStorage.setItem(
                agentPromptDraftStorageKey,
                JSON.stringify({
                    prompt,
                    updatedAt: Date.now(),
                    ...(promptDraftMeta.lastSuggestionId ? { lastSuggestionId: promptDraftMeta.lastSuggestionId } : {}),
                    ...(promptDraftMeta.specialization ? { specialization: promptDraftMeta.specialization } : {}),
                    ...(scopeAnswers && Object.keys(scopeAnswers).length > 0 ? { scopeAnswers } : {}),
                    ...(selectedMonitoringId ? { monitoringId: selectedMonitoringId } : {}),
                    ...(selectedMonitoringUrl ? { monitoringUrl: selectedMonitoringUrl } : {}),
                    ...(monitoringEnabledForRun ? { monitoringEnabledForRun: true } : {}),
                    ...(scopeContext?.memoryDerived ? { memoryDerived: true } : {})
                } satisfies PromptDraftPayload)
            );
        } catch (error) {
            console.warn('[AgentPromptCard] Failed to persist prompt draft:', error);
        }
    }, [agentPromptDraftStorageKey, prompt, promptDraftMeta, scopeAnswers, selectedMonitoringId, selectedMonitoringUrl, monitoringEnabledForRun, scopeContext?.memoryDerived]);

    // Load preferences from API
    const { data: prefsData } = useQuery<{ data: { agent: any } }>({
        queryKey: ['agent-preferences', userId || 'anonymous'],
        queryFn: () => apiClient.get('/agent/preferences'),
        enabled: !!userId && !isFreeLikeUser,
        staleTime: 5 * 60 * 1000,
    });

    // Fetch GitHub repos
    const { data: repos, isLoading: isLoadingRepos, error: reposError } = useQuery<GitHubRepo[], Error>({
        queryKey: ['github-repos', userId || 'anonymous'],
        queryFn: async () => {
            const response = await apiClient.get<any>('/github/repos');
            let data: any[] = [];

            // Robust handling for wrapped response vs direct array
            if (response?.data && Array.isArray(response.data)) {
                data = response.data;
            } else if (Array.isArray(response)) {
                data = response;
            } else {
                console.warn('[AgentPromptCard] Unexpected repos response format:', response);
                return [];
            }

            return data;
        },
        enabled: !!userId && !isFreeLikeUser,
        staleTime: 5 * 60 * 1000,
        retry: false,
    });

    // Fetch branches for selected repo (using tree endpoint which returns branches)
    const { data: branchesResponse, isLoading: isLoadingBranches } = useQuery<{ branches?: GitHubBranch[]; data?: GitHubBranch[] }, Error>({
        queryKey: ['github-branches', selectedRepo, userId || 'anonymous'],
        queryFn: async () => {
            try {
                // Split owner/repo for the path
                const [owner, repo] = selectedRepo.split('/');
                if (!owner || !repo) {
                    console.warn('[AgentPromptCard] Invalid repo format:', selectedRepo);
                    return { branches: [] };
                }
                // Use tree endpoint which returns branches
                return await apiClient.get(`/github/repos/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/tree`);
            } catch (error) {
                // Silently handle expected 404s for repos the app can't access
                console.warn(`[AgentPromptCard] Failed to fetch branches for ${selectedRepo}`, error);
                return { branches: [] };
            }
        },
        enabled: !!userId && !isFreeLikeUser && !!selectedRepo && shouldFetchBranches && !!repos?.find(r => r.full_name === selectedRepo),
        staleTime: 2 * 60 * 1000,
        retry: false,
    });

    // Extract branches from the tree response (tree endpoint returns { branches: [...] })
    // Ensure we handle cases where branches/data is not an array
    const rawBranches = branchesResponse?.branches || branchesResponse?.data || [];
    const branches: GitHubBranch[] = Array.isArray(rawBranches)
        ? rawBranches.map((b: any) => typeof b === 'string' ? { name: b, protected: false } : b)
        : [];
    const dashboardRepoSignals = useMemo(
        () => ({
            ...(selectedRepo ? { repoFullName: selectedRepo } : {}),
            ...(selectedBranch ? { branch: selectedBranch } : {}),
            frameworkHints: [
                selectedRepo.includes('next') ? 'next.config' : '',
                selectedRepo.includes('vite') ? 'vite.config' : '',
                selectedRepo.includes('react') ? 'react' : '',
            ].filter(Boolean)
        }),
        [selectedRepo, selectedBranch]
    );
    const { data: superMemoryResponse } = useQuery<{ data: AgentSuperMemoryState }>({
        queryKey: ['agent-memory', 'scope', userId || 'anonymous', selectedRepo || '_none_'],
        queryFn: () => apiClient.get('/agent/memory'),
        enabled: !!userId && !isFreeLikeUser && !!selectedRepo,
        staleTime: 5 * 60 * 1000,
        retry: false
    });
    const { data: monitoringResponse } = useQuery<any>({
        queryKey: ['agent-monitoring-options', userId || 'anonymous'],
        queryFn: () => apiClient.get('/monitoring'),
        enabled: !!userId && !isFreeLikeUser,
        staleTime: 3 * 60 * 1000,
        retry: false
    });
    const extractMonitoringRows = (response: any): any[] => {
        const candidates = [
            response,
            response?.data,
            response?.data?.data,
            response?.monitorings,
            response?.data?.monitorings,
            response?.items,
            response?.data?.items,
            response?.rows,
            response?.data?.rows
        ];
        for (const candidate of candidates) {
            if (Array.isArray(candidate)) return candidate;
        }
        return [];
    };
    const monitoringRows = useMemo<any[]>(
        () => extractMonitoringRows(monitoringResponse),
        [monitoringResponse]
    );
    const monitoringOptions = useMemo(
        () => monitoringRows
            .filter((row) =>
                typeof row?.id === 'string' &&
                (typeof row?.website_url === 'string' || typeof row?.websiteUrl === 'string')
            )
            .map((row) => ({
                id: row.id as string,
                websiteUrl: String(row.website_url || row.websiteUrl),
                formFactor: (String(row?.form_factor || row?.additional_metrics?.form_factor || 'mobile').toLowerCase() === 'desktop'
                    ? 'desktop'
                    : 'mobile') as 'desktop' | 'mobile'
            })),
        [monitoringRows]
    );
    const memoryEntries = useMemo(() => {
        const memoryState = superMemoryResponse?.data;
        if (!memoryState || memoryState.enabled === false) return [];
        return getRepoScopedMemoryEntries(memoryState.entries || [], selectedRepo);
    }, [superMemoryResponse, selectedRepo]);
    const scopeSuggestionModel = useMemo(
        () => buildPersonalizedScopeSuggestions({
            prompt,
            repoFullName: selectedRepo,
            branch: selectedBranch,
            repoSignals: dashboardRepoSignals,
            memoryEntries,
            scopeAnswers
        }),
        [prompt, selectedRepo, selectedBranch, dashboardRepoSignals, memoryEntries, scopeAnswers]
    );
    const shouldShowLocalScopeSuggestions = useMemo(() => {
        if (scopeGatePayload) return true;
        if (!prompt.trim()) return false;
        return isLikelyVaguePrompt(prompt);
    }, [scopeGatePayload, prompt]);

    // Task creation mutation
    const createTaskMutation = useMutation({
        mutationFn: async (data: {
            prompt: string;
            repoFullName: string;
            branch: string;
            mode: string;
            monitoringId?: string;
            repoSignals?: {
                repoFullName?: string;
                branch?: string;
                frameworkHints?: string[];
            };
            scopeContext?: AgentExecuteScopeContext;
        }) => {
            return apiClient.post('/agent/execute', data);
        },
        onSuccess: (response: any) => {
            if (response?.data?.taskId) {
                const intakeWarnings = Array.isArray(response?.data?.intakeWarnings)
                    ? response.data.intakeWarnings
                    : [];
                const actionableWarnings = intakeWarnings.filter(isActionableIntakeWarning);
                if (actionableWarnings.length > 0) {
                    toast.warning(actionableWarnings[0]?.message || 'Execution started with intake warnings.');
                }
                clearPromptDraft();
                setScopeGatePayload(null);
                setScopeContext(null);
                setScopeAnswers({});
                setPromptDraftMeta({});
                navigate(`/ agent ? taskId = ${response.data.taskId}`);
            }
        },
        onError: (error: any) => {
            const scopePayload = extractPromptScopePayload(error);
            if (scopePayload) {
                setScopeGatePayload(scopePayload);
                toast.info(scopePayload.message || 'Add a bit more scope before running execution.');
                return;
            }
            toast.error(error?.message || 'Failed to start agent execution.');
        }
    });

    // Auto-select first repo and its default branch
    useEffect(() => {
        if (repos && repos.length > 0 && !selectedRepo) {
            // Check for API preferences first
            const apiDefaultRepo = prefsData?.data?.agent?.defaultRepo;
            const apiDefaultBranch = prefsData?.data?.agent?.defaultBranch;

            if (apiDefaultRepo && repos.find(r => r.full_name === apiDefaultRepo)) {
                setSelectedRepo(apiDefaultRepo);
                setSelectedBranch(apiDefaultBranch || repos.find(r => r.full_name === apiDefaultRepo)?.default_branch || 'main');
                setShouldFetchBranches(true); // Preload for API default
                return;
            }

            const firstRepo = repos[0];
            setSelectedRepo(firstRepo.full_name);
            setSelectedBranch(firstRepo.default_branch);
            setShouldFetchBranches(false); // Lazy load for auto-selected fallback
        }
    }, [repos, selectedRepo, prefsData]);

    // Update branch when repo changes
    useEffect(() => {
        if (selectedRepo && repos && repos.length > 0) {
            const repo = repos.find(r => r.full_name === selectedRepo);
            if (repo && !selectedBranch) {
                setSelectedBranch(repo.default_branch);
            }
        }
    }, [selectedRepo, repos, selectedBranch]);

    const submitPrompt = (promptValue: string, overrideScopeContext?: AgentExecuteScopeContext | null) => {
        const trimmedPrompt = promptValue.trim();
        if (!trimmedPrompt || !selectedRepo) return;
        const effectiveScopeContext = overrideScopeContext !== undefined ? overrideScopeContext : scopeContext;
        const fallbackScopeAnswers = scopeAnswers && Object.keys(scopeAnswers).length > 0
            ? scopeAnswers
            : undefined;
        const resolvedScopeContext = effectiveScopeContext || (fallbackScopeAnswers
            ? {
                source: 'dashboard_card',
                scopeAnswers: fallbackScopeAnswers,
                ...(scopeSuggestionModel.memoryDerived ? { memoryDerived: true } : {})
            }
            : null);
        const resolvedMonitoringId = monitoringEnabledForRun
            ? selectedMonitoringId || resolvedScopeContext?.monitoringId || undefined
            : undefined;
        const resolvedMonitoringUrl = monitoringEnabledForRun
            ? selectedMonitoringUrl || resolvedScopeContext?.monitoringUrl || undefined
            : undefined;
        const scopeContextWithoutMonitoring = resolvedScopeContext
            ? { ...resolvedScopeContext }
            : null;
        if (scopeContextWithoutMonitoring) {
            delete (scopeContextWithoutMonitoring as Partial<AgentExecuteScopeContext>).monitoringId;
            delete (scopeContextWithoutMonitoring as Partial<AgentExecuteScopeContext>).monitoringUrl;
        }

        if (createTaskOnSubmit) {
            createTaskMutation.mutate({
                prompt: trimmedPrompt,
                repoFullName: selectedRepo,
                branch: selectedBranch,
                mode: executionMode,
                monitoringId: resolvedMonitoringId,
                repoSignals: dashboardRepoSignals,
                scopeContext: resolvedScopeContext
                    ? {
                        ...(scopeContextWithoutMonitoring || {}),
                        ...(resolvedMonitoringId ? { monitoringId: resolvedMonitoringId } : {}),
                        ...(resolvedMonitoringUrl ? { monitoringUrl: resolvedMonitoringUrl } : {})
                    }
                    : undefined,
            });
            return;
        }

        navigate('/agent', {
            state: {
                repoFullName: selectedRepo,
                branch: selectedBranch,
                prompt: trimmedPrompt,
                autoStart: true,
                executionMode: executionMode,
                scopeContext: resolvedScopeContext
                    ? {
                        ...(scopeContextWithoutMonitoring || {}),
                        ...(resolvedMonitoringId ? { monitoringId: resolvedMonitoringId } : {}),
                        ...(resolvedMonitoringUrl ? { monitoringUrl: resolvedMonitoringUrl } : {})
                    }
                    : undefined,
            }
        });
    };

    const handleSubmit = () => {
        if (isFreeLikeUser) {
            toast.error('Upgrade your account to use Agent and connect GitHub.');
            navigate('/subscriptions');
            return;
        }
        if (!prompt.trim() || !selectedRepo) return;

        if (!createTaskOnSubmit && isLikelyVaguePrompt(prompt)) {
            const specialization = scopeSuggestionModel.specialization || detectPromptSpecialization(prompt);
            setScopeGatePayload({
                success: false,
                error: 'prompt_needs_scope',
                code: 'prompt_needs_scope',
                message: 'Add a bit more scope before starting execution.',
                reason: 'Prompt is too generic. Add the symptom, target area, and expected outcome.',
                specialization,
                suggestions: scopeSuggestionModel.suggestions,
                requiredHints: scopeSuggestionModel.requiredHints,
            });
            return;
        }

        setScopeGatePayload(null);
        submitPrompt(prompt);
    };

    const applyScopeSuggestion = (suggestion: PromptScopeSuggestion, runImmediately: boolean) => {
        const nextScopeAnswers = scopeAnswers && Object.keys(scopeAnswers).length > 0
            ? scopeAnswers
            : undefined;
        const nextScopeContext: AgentExecuteScopeContext = {
            suggestionId: suggestion.id,
            specialization: suggestion.category,
            source: 'dashboard_card',
            ...(nextScopeAnswers ? { scopeAnswers: nextScopeAnswers } : {}),
            ...(monitoringEnabledForRun && selectedMonitoringId ? { monitoringId: selectedMonitoringId } : {}),
            ...(monitoringEnabledForRun && selectedMonitoringUrl ? { monitoringUrl: selectedMonitoringUrl } : {}),
            ...(scopeSuggestionModel.memoryDerived ? { memoryDerived: true } : {}),
        };
        setPrompt(suggestion.value);
        setScopeContext(nextScopeContext);
        setPromptDraftMeta({
            lastSuggestionId: suggestion.id,
            specialization: suggestion.category,
        });
        setScopeGatePayload((current) => current
            ? {
                ...current,
                specialization: suggestion.category,
            }
            : current);
        if (runImmediately) {
            submitPrompt(suggestion.value, nextScopeContext);
        }
    };

    const handleRepoChange = (value: string) => {
        setSelectedRepo(value);
        setShouldFetchBranches(true); // Always fetch when user manually selects
        // Reset branch to default for new repo
        if (repos) {
            const repo = repos.find(r => r.full_name === value);
            if (repo) {
                setSelectedBranch(repo.default_branch);
            }
        }
    };

    // If GitHub not connected, show connect prompt
    if (reposError) {
        return (
            <Card className={`bg - gradient - to - br from - primary / 5 to - primary / 10 border - primary / 20 ${className} `}>
                <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                            <Github className="h-5 w-5 text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">Connect GitHub to analyze code</span>
                        </div>
                        <Button variant="outline" size="sm" onClick={handleConnectGitHub}>
                            Connect GitHub
                        </Button>
                    </div>
                </CardContent>
            </Card>
        );
    }

    if (isFreeLikeUser) {
        return (
            <Card className={`bg - gradient - to - br from - primary / 5 to - primary / 10 border - primary / 20 ${className} `}>
                <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                            <Github className="h-5 w-5 text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">Upgrade to use Agent Workspace</span>
                        </div>
                        <Button variant="outline" size="sm" onClick={handleConnectGitHub}>
                            View Plans
                        </Button>
                    </div>
                </CardContent>
            </Card>
        );
    }

    const isSubmitting = createTaskMutation.isPending;
    const selectedModeInfo = EXECUTION_MODES.find(m => m.value === executionMode) || EXECUTION_MODES[0];

    return (
        <Card className={`bg - gradient - to - br from - background to - muted / 30 border - border ${className} `}>
            <CardContent className="pt-4 pb-4">
                {/* Header: Repo selector + Configure */}
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                        {isLoadingRepos ? (
                            <Skeleton className="h-8 w-48" />
                        ) : (
                            <Select value={selectedRepo} onValueChange={handleRepoChange}>
                                <SelectTrigger className="w-auto min-w-[200px] bg-background">
                                    <SelectValue placeholder="Select repository..." />
                                </SelectTrigger>
                                <SelectContent className="max-h-[300px] overflow-y-auto">
                                    {repos && repos.map((repo) => (
                                        <SelectItem key={repo.id} value={repo.full_name}>
                                            <span className="font-mono text-sm">{repo.full_name}</span>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        )}
                    </div>
                    <Button
                        variant="ghost"
                        size="sm"
                        className="text-muted-foreground text-xs gap-1"
                        onClick={() => navigate('/agent/settings?tab=environment')}
                    >
                        <Settings className="h-3 w-3" />
                        Settings
                    </Button>
                </div>

                {/* Prompt textarea */}
                <div className="mb-3">
                    <textarea
                        value={prompt}
                        onChange={(e) => {
                            setPrompt(e.target.value);
                            setScopeGatePayload(null);
                            setScopeContext(null);
                            setPromptDraftMeta({});
                        }}
                        placeholder="Help me fix this error..."
                        className="w-full h-24 p-3 rounded-lg bg-muted/50 border border-border text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/50 placeholder:text-muted-foreground"
                        disabled={isSubmitting}
                    />
                </div>
                {shouldShowLocalScopeSuggestions && (
                    <div className="mb-3">
                        <PromptScopeSuggestions
                            layoutMode="builder"
                            reason={scopeGatePayload?.reason || 'Add a bit more scope so the agent can target the right files.'}
                            specialization={scopeGatePayload?.specialization || scopeSuggestionModel.specialization}
                            suggestions={scopeGatePayload?.suggestions || scopeSuggestionModel.suggestions}
                            requiredHints={scopeGatePayload?.requiredHints || scopeSuggestionModel.requiredHints}
                            scopeAnswers={scopeAnswers}
                            scopeAnswerOptions={scopeSuggestionModel.answerOptions}
                            memoryDerived={scopeSuggestionModel.memoryDerived}
                            onScopeAnswersChange={(answers) => {
                                setScopeAnswers(answers);
                                setScopeContext((current) => current
                                    ? {
                                        ...current,
                                        scopeAnswers: Object.keys(answers).length > 0 ? answers : undefined,
                                        monitoringId: monitoringEnabledForRun && selectedMonitoringId ? selectedMonitoringId : undefined,
                                        monitoringUrl: monitoringEnabledForRun && selectedMonitoringUrl ? selectedMonitoringUrl : undefined,
                                        ...(scopeSuggestionModel.memoryDerived ? { memoryDerived: true } : {}),
                                    }
                                    : current);
                            }}
                            monitoringOptions={monitoringOptions}
                            selectedMonitoringId={selectedMonitoringId}
                            onSelectMonitoring={(selection) => {
                                setSelectedMonitoringId(selection?.id || '');
                                setSelectedMonitoringUrl(selection?.websiteUrl || '');
                                setMonitoringEnabledForRun(!!selection);
                                setScopeContext((current) => current
                                    ? {
                                        ...current,
                                        monitoringId: selection?.id || undefined,
                                        monitoringUrl: selection?.websiteUrl || undefined
                                    }
                                    : current);
                            }}
                            disabled={isSubmitting}
                            onUsePrompt={(suggestion) => applyScopeSuggestion(suggestion, false)}
                            onUseAndRun={(suggestion) => applyScopeSuggestion(suggestion, true)}
                        />
                    </div>
                )}

                {/* Footer: Branch + Mode + Submit */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <span className="text-muted-foreground">+</span>
                        {/* Branch selector */}
                        {isLoadingBranches ? (
                            <Skeleton className="h-6 w-20" />
                        ) : (
                            <Select
                                value={selectedBranch}
                                onValueChange={setSelectedBranch}
                                onOpenChange={() => setShouldFetchBranches(true)} // Lazy load trigger
                            >
                                <SelectTrigger className="h-7 w-auto min-w-[100px] bg-muted border-0 text-xs gap-1">
                                    <GitBranch className="h-3 w-3" />
                                    <SelectValue placeholder="Branch" />
                                </SelectTrigger>
                                <SelectContent>
                                    {branches.map((branch) => (
                                        <SelectItem key={branch.name} value={branch.name}>
                                            <span className="text-xs">{branch.name}</span>
                                        </SelectItem>
                                    ))}
                                    {branches.length === 0 && selectedBranch && (
                                        <SelectItem value={selectedBranch}>
                                            <span className="text-xs">{selectedBranch}</span>
                                        </SelectItem>
                                    )}
                                </SelectContent>
                            </Select>
                        )}
                    </div>

                    {/* Right: Mode dropdown + Submit arrow */}
                    <div className="flex items-center gap-1">
                        {/* Mode dropdown (Review / Auto) */}
                        <Select value={executionMode} onValueChange={(v) => setExecutionMode(v as ExecutionMode)} disabled={isSubmitting}>
                            <SelectTrigger className="h-8 w-auto min-w-[100px] bg-muted/50 border-0 gap-1 text-xs px-2">
                                <selectedModeInfo.icon className="h-3 w-3" />
                                <span className="text-xs">{selectedModeInfo.label}</span>
                            </SelectTrigger>
                            <SelectContent align="end" className="w-64">
                                {EXECUTION_MODES.map((mode) => (
                                    <SelectItem key={mode.value} value={mode.value} className="py-2">
                                        <div className="flex items-start gap-3">
                                            <mode.icon className="h-4 w-4 mt-0.5 flex-shrink-0" />
                                            <div>
                                                <div className="font-medium text-sm flex items-center gap-2">
                                                    {mode.label}
                                                    {mode.value === executionMode && (
                                                        <CheckCircle2 className="h-3 w-3 text-primary ml-auto" />
                                                    )}
                                                </div>
                                                <p className="text-[10px] text-muted-foreground mt-0.5">{mode.description}</p>
                                            </div>
                                        </div>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>

                        {/* Submit arrow button */}
                        <Button
                            size="icon"
                            className="h-8 w-8"
                            disabled={!prompt.trim() || !selectedRepo || isSubmitting}
                            onClick={handleSubmit}
                        >
                            {isSubmitting ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <ArrowRight className="h-4 w-4" />
                            )}
                        </Button>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}
